import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { formatQuotationId } from '../utils/displayIds';

const DEFAULT_HEADER_FILL = '#356167';

function hexToRgb(hex) {
    const normalized = String(hex || DEFAULT_HEADER_FILL).replace('#', '');
    if (!/^[0-9A-Fa-f]{6}$/.test(normalized)) {
        return [53, 97, 103];
    }
    return [
        parseInt(normalized.slice(0, 2), 16),
        parseInt(normalized.slice(2, 4), 16),
        parseInt(normalized.slice(4, 6), 16),
    ];
}

function contrastTextColor(rgb) {
    const [r, g, b] = rgb;
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? [20, 20, 20] : [255, 255, 255];
}

const generateQuotationPDF = async (quotationData, pieCharts, base64Logo, logoUrl, headerFillColor = DEFAULT_HEADER_FILL) => {
    const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4', compress: true });

    const PAGE_WIDTH = 210;
    const PAGE_HEIGHT = 297;
    const MARGIN = 10;
    // Calculate space required for "Powered by Wandawazi" section (logo + text)
    const POWERED_BY_HEIGHT = 18; // 8mm logo + 5mm text + 5mm margin
    const CONTENT_BOTTOM = PAGE_HEIGHT - POWERED_BY_HEIGHT - 10;

    const SIGNATURE_BLOCK_HEIGHT = 70;
    const SAFE_BOTTOM = PAGE_HEIGHT - SIGNATURE_BLOCK_HEIGHT - 10;

    const headerFillRgb = hexToRgb(headerFillColor);
    const headerTextRgb = contrastTextColor(headerFillRgb);
    const brandedHeadStyles = {
        fillColor: headerFillRgb,
        textColor: headerTextRgb,
        fontStyle: 'bold',
    };

    let yPos = 20;

    /* ---------------- HELPERS ---------------- */

    const addLogoToPage = async () => {
        const isValidBase64 = base64Logo && typeof base64Logo === 'string' && base64Logo.startsWith('data:image/');

        const loadAndAdd = (src, options = {}) => new Promise((resolve) => {
            const img = new Image();
            if (options.crossOrigin) img.crossOrigin = options.crossOrigin;
            img.onload = () => {
                const aspect = img.naturalWidth / img.naturalHeight;
                const height = 18;
                const width = height * aspect;
                try {
                    if (options.useCanvas) {
                        const canvas = document.createElement('canvas');
                        canvas.width = img.naturalWidth;
                        canvas.height = img.naturalHeight;
                        canvas.getContext('2d').drawImage(img, 0, 0);
                        const dataUrl = canvas.toDataURL('image/png');
                        doc.addImage(dataUrl, 'PNG', PAGE_WIDTH - width - 10, 10, width, height, 'tenant-logo');
                    } else {
                        const format = typeof src === 'string' && src.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
                        doc.addImage(src, format, PAGE_WIDTH - width - 10, 10, width, height, 'tenant-logo');
                    }
                    resolve(true);
                } catch (e) {
                    console.error('Failed to add logo to PDF:', e);
                    resolve(false);
                }
            };
            img.onerror = () => resolve(false);
            img.src = src;
        });

        if (isValidBase64 && await loadAndAdd(base64Logo)) return;
        if (logoUrl && await loadAndAdd(logoUrl, { crossOrigin: 'anonymous', useCanvas: true })) return;
        // No tenant logo configured — leave the top-right blank
    };

    const addPoweredByLogo = async () => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                const aspect = img.width / img.height;
                const height = 8;
                const width = height * aspect;

                // Position at bottom right
                const x = PAGE_WIDTH - width - 10;
                const y = PAGE_HEIGHT - height - 10;

                doc.addImage(img.src, 'PNG', x, y, width, height, 'wandawazi-logo');

                // Add "Powered by Wandawazi" text below logo
                doc.setFontSize(8);
                doc.setTextColor(100);
                const text = 'Powered by Wandawazi';
                const textWidth = doc.getTextWidth(text);
                doc.text(text, PAGE_WIDTH - textWidth - 10, y + height + 5);
                doc.setTextColor(0); // Reset to black
                resolve();
            };
            img.src = '/Wandawazi Logo Transparent.png';
        });
    };

    const addNewPageWithLogo = async () => {
        doc.addPage();
        await addLogoToPage();
        await addPoweredByLogo();
        yPos = 30;
    };

    // Set a global didDrawPage callback to ensure logo is added to every page
    doc.autoTable({
        head: [],
        body: [],
        startY: -1000, // Off-page to prevent rendering
        didDrawPage: async (data) => {
            await addLogoToPage();
            await addPoweredByLogo();
        }
    });

    /* ================= PAGE 1 ================= */

    await addLogoToPage();
    await addPoweredByLogo();

    doc.setFontSize(18);
    doc.text('QUOTATION REPORT', MARGIN, yPos);
    yPos += 8;

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(formatQuotationId(quotationData.QuotationID), MARGIN, yPos);
    doc.setTextColor(0);
    yPos += 10;

    /* -------- Customer Details -------- */

    // Helper function to format currency with space as thousand separator and decimal point
    const formatCurrency = (value) => {
        const num = parseFloat(value);
        const parts = num.toFixed(2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        return parts.join('.');
    };

    const customerDetailsBody = [
        ['Customer', quotationData.CustomerName],
        quotationData.RoomNumber && ['Room Number', quotationData.RoomNumber],
        ['Report Date', quotationData.ReportDate],
        ['Total Monthly Fee', `R${formatCurrency(quotationData.TotalMonthlyFee)}`],
        parseFloat(quotationData.CustomerDiscount) > 0 && ['Discount', `${quotationData.CustomerDiscount}%`],
        parseFloat(quotationData.CustomerDiscount) > 0 && ['Final Quoted Price', `R${formatCurrency(quotationData.FinalQuotedPrice)}`],
    ].filter(Boolean);

    doc.autoTable({
        startY: yPos,
        head: [['Customer Details', 'Value']],
        body: customerDetailsBody,
        theme: 'grid',
        headStyles: brandedHeadStyles,
        margin: { left: MARGIN, right: MARGIN, bottom: POWERED_BY_HEIGHT + 10 },
        pageBreak: 'auto',
        rowPageBreak: 'auto'
    });

    yPos = doc.lastAutoTable.finalY + 10;

    /* -------- Service Plan Activities (UNDER customer details) -------- */

    doc.autoTable({
        startY: yPos,
        head: [['Service Plan Activities']],
        body: quotationData.CarePlanActivities.map(a => [a]),
        theme: 'grid',
        headStyles: brandedHeadStyles,
        margin: { left: MARGIN, right: MARGIN, bottom: POWERED_BY_HEIGHT + 10 },
        pageBreak: 'auto',
        rowPageBreak: 'auto'
    });

    // IMPORTANT: update yPos correctly
    yPos = doc.lastAutoTable.finalY + 10;


    if (yPos > SAFE_BOTTOM) {
        await addNewPageWithLogo();
    }

    /* -------- Quoted Services -------- */

    doc.autoTable({
        startY: yPos,
        head: [['Quoted Services', 'Fee (R)']],
        body: quotationData.QuotedServices.length
            ? quotationData.QuotedServices.map(s => [s.name, formatCurrency(s.fee)])
            : [['No Services Quoted', '']],
        theme: 'grid',
        headStyles: brandedHeadStyles,
        margin: { left: MARGIN, right: MARGIN, bottom: POWERED_BY_HEIGHT + 10 },
        pageBreak: 'auto',
        rowPageBreak: 'auto',
        styles: {
            fontSize: 10,
        },
        columnStyles: {
            0: { halign: 'left' },
            1: { halign: 'right' },
        }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    /* -------- Additional Notes -------- */

    if (quotationData.AdditionalNotes) {
        // Always start Additional Notes on a new page
        await addNewPageWithLogo();

        doc.setFontSize(14);
        doc.text('Additional Notes', MARGIN, yPos);
        yPos += 8;

        // Helper function to count words from HTML content
        const countWords = (html) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const text = tempDiv.textContent || tempDiv.innerText || '';
            return text.trim().split(/\s+/).filter(word => word.length > 0).length;
        };

        // Helper function to truncate HTML content to word limit
        const truncateToWordLimit = (html, wordLimit) => {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = html;
            const text = tempDiv.textContent || tempDiv.innerText || '';
            const words = text.trim().split(/\s+/).filter(word => word.length > 0);
            
            if (words.length <= wordLimit) {
                return html;
            }
            
            const truncatedText = words.slice(0, wordLimit).join(' ') + '...';
            return truncatedText;
        };

        const WORD_LIMIT = 850;
        const wordCount = countWords(quotationData.AdditionalNotes);
        
        // Truncate content if it exceeds the word limit
        const notesContent = wordCount > WORD_LIMIT
            ? truncateToWordLimit(quotationData.AdditionalNotes, WORD_LIMIT)
            : quotationData.AdditionalNotes;

        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = notesContent;
        tempDiv.style.width = '180mm';
        tempDiv.style.fontSize = '10px';
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, { scale: 1, backgroundColor: '#fff' });
        document.body.removeChild(tempDiv);

        const imgData = canvas.toDataURL('image/jpeg', 0.65);
        const imgWidth = 180;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        doc.addImage(imgData, 'JPEG', MARGIN, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 10;
    }

    /* -------- SIGNATURES (ALWAYS BOTTOM) -------- */

    // Ensure signatures never collide with content
    if (yPos > SAFE_BOTTOM) {
        await addNewPageWithLogo();
    } else {
        // Ensure current page has powered by logo
        await addPoweredByLogo();
    }

    const signatureTop = PAGE_HEIGHT - SIGNATURE_BLOCK_HEIGHT;

    doc.setFontSize(16);
    doc.text('Signatures', MARGIN, signatureTop);

    doc.setFontSize(12);

    // Client
    doc.text('CLIENT', MARGIN, signatureTop + 12);
    doc.text('Name:', MARGIN, signatureTop + 22);
    doc.text(quotationData.signatures?.clientName || '_____________', MARGIN + 25, signatureTop + 22);
    doc.text('Signature:', MARGIN, signatureTop + 32);
    doc.text('_____________', MARGIN + 25, signatureTop + 32);
    doc.text('Date:', MARGIN, signatureTop + 42);
    doc.text(quotationData.signatures?.clientDate || '_____________', MARGIN + 25, signatureTop + 42);

    // Company
    doc.text('COMPANY REPRESENTATIVE', 110, signatureTop + 12);
    doc.text('Name:', 110, signatureTop + 22);
    doc.text(quotationData.signatures?.companyName || '_____________', 135, signatureTop + 22);
    doc.text('Signature:', 110, signatureTop + 32);
    doc.text('_____________', 135, signatureTop + 32);
    doc.text('Date:', 110, signatureTop + 42);
    doc.text(quotationData.signatures?.companyDate || '_____________', 135, signatureTop + 42);

    /* ================= PAGE 3 (CHARTS ONLY) ================= */

    await addNewPageWithLogo();

    doc.setFontSize(18);
    doc.text('Quotation Summary Details', MARGIN, yPos);
    yPos += 10;

    const cellWidth = (PAGE_WIDTH - 20) / 2;
    const cellHeight = 70;

    let index = 0;

    for (let row = 0; row < 3; row++) {
        for (let col = 0; col < 2; col++) {
            if (!pieCharts[index]) continue;

            // Get the parent .chart-item element which includes the h2 header
            const el = pieCharts[index].closest('.chart-item');
            const canvas = await html2canvas(el, { scale: 1, backgroundColor: '#fff' });
            const img = canvas.toDataURL('image/jpeg', 0.65);

            const x = MARGIN + col * cellWidth;
            const y = yPos + row * cellHeight;

            const ratio = canvas.width / canvas.height;
            let w = cellWidth - 10;
            let h = w / ratio;

            if (h > cellHeight - 10) {
                h = cellHeight - 10;
                w = h * ratio;
            }

            // Check if chart would overlap with powered by logo
            if (y + h > CONTENT_BOTTOM) {
                await addNewPageWithLogo();
                // Recalculate y position for new page
                yPos = 30;
                row = 0;
                col = -1; // Reset column to -1 since loop will increment to 0
                index--; // Decrement index to reprocess this chart
                continue;
            }

            doc.addImage(img, 'JPEG', x + (cellWidth - w) / 2, y + (cellHeight - h) / 2, w, h);
            index++;
        }
    }

    doc.save(`Quotation_${quotationData.CustomerName}.pdf`);
};

export default generateQuotationPDF;
